/*
 * Copyright (c) 2023. iSecNew10 Enterprises LLC
 */

import React, {useEffect, useLayoutEffect, useState} from 'react'

import './index.css'

import {Outlet, useLocation, useOutletContext} from 'react-router-dom'
import {Helmet} from 'react-helmet';
import {ReactNotifications} from 'react-notifications-component'

import Header from './Header'
import Footer from './Footer'
import Constants from '../../Constants';
import Route from '../../types/Route';
import TrustImages from './Header/TrustImages/TrustImages';
import Routes from '../../Routes';
import BlogUtil from '../../utils/Blog';

type ContextType = { width: number };

const Layout = (props: {
    width: number,
}) => {
    const {width} = props;

    const location = useLocation();

    const getPathComponents = () => {
        return location.pathname === '/' ? null : location.pathname.split('/').filter(r => r && r.trim().length > 0)
    }
    const getPathComponent = (index: number): string | null => {
        const pathComponents = getPathComponents();
        if (pathComponents && pathComponents.length > index) {
            return typeof pathComponents[index] === 'string'
                ? pathComponents[index]
                : null;
        }
        return null;
    }
    const getActionPath = () => {
        return getPathComponent(0);
    }

    const getCurrentRoute = (): Route => {
        const actionPath = getActionPath();
        if (!actionPath || actionPath.length < 1 || actionPath === '/') {
            return Routes.Routes.find(route => route.index)!;
        }
        if (actionPath === 'magazin' && getPathComponent(1) !== null) {
            return Routes.getRouteByPath(Routes.BlogArticleRouteSlug)!;
        }
        return Routes.getRouteByPath(actionPath) ?? Routes.getRouteByPath('*')!
    }

    const getHeaderImage = () => {
        const currentRoute = getCurrentRoute()
        if (typeof currentRoute.headerImage === 'function') {
            return currentRoute.headerImage(width);
        }
        return null;
    }

    const getInformationContainerItem = () => {
        const currentRoute = getCurrentRoute()
        if (typeof currentRoute.informationContainerItem === 'object') {
            return currentRoute.informationContainerItem;
        }
        return null;
    }

    const getCanonicalUrl = (): string => {
        return Constants.SITE_URL + '/' + (
            currentRoute.path === Constants.BlogArticleRouteSlug
                ? BlogUtil.getArticleUrlBySlug(getPathComponent(1)!)
                : currentRoute.path
        );
    }

    const getHeaderOffset = (): number => {
        const header = document.getElementById('header');
        const informationContainer = document.getElementById('mainInformationContainer');
        let headerValue = 0;
        let informationContainerValue = 0;
        if (header) {
            headerValue = header.getBoundingClientRect().bottom
        }
        if (informationContainer) {
            const rect = informationContainer.getBoundingClientRect();
            informationContainerValue = rect.bottom + 25
        }
        return informationContainerValue > headerValue ? informationContainerValue - headerValue : 0;
    }

    const getMainContentOffset = (): number => {
        if (width < Constants.LayoutBreakpoints.desktop) {
            return 0;
        }
        const header = document.getElementById('header');
       // const trustsContainer = document.getElementById('trustsContainer');
        let headerValue = 0;
        const trustsContainerValue = 0;
        if (header) {
            headerValue = header.getBoundingClientRect().bottom
        }
        headerValue = getHeaderOffset();
        return trustsContainerValue > headerValue ? (trustsContainerValue - headerValue) + 25 : 25;
    }

    const [headerOffset, setHeaderOffset] = useState(getHeaderOffset());
    const [mainContentOffset, setMainContentOffset] = useState(getMainContentOffset);
    const [currentActionPath, setCurrentActionPath] = useState(getActionPath());
    const [currentRoute, setCurrentRoute] = useState(getCurrentRoute());
    const [currentCanonicalUrl, setCurrentCanonicalUrl] = useState(getCanonicalUrl());
    const [image, setImage] = useState(getHeaderImage());
    const [informationContainerItem, setInformationContainerItem] = useState(getInformationContainerItem());

    const updateRoute = () => setCurrentRoute(getCurrentRoute())
    const updateCanonicalUrl = () => setCurrentCanonicalUrl(getCanonicalUrl())
    const updateHeaderImage = () => setImage(getHeaderImage())
    const updateHeaderInformationContainerItem = () => setInformationContainerItem(getInformationContainerItem())
    const updateHeaderOffset = () => setHeaderOffset(getHeaderOffset())
    const updatedMainContentOffset = () => setMainContentOffset(getMainContentOffset())

    const updateOffsets = () => {
        setTimeout(() => {
            updateHeaderOffset()
            setTimeout(() => {
                updatedMainContentOffset()
            }, 200)
        }, 200)
    }

    useLayoutEffect(() => {
        updateOffsets()
    }, []);

    const updateAll = () => {
        updateHeaderImage()
        updateRoute()
        updateCanonicalUrl()
        updateHeaderInformationContainerItem()
        updateHeaderOffset()
        updatedMainContentOffset()
        updateOffsets()
    }

    useEffect(() => {
        updateAll()
    }, [width]);
    useEffect(() => {
        Constants.scrollToTop()
        updateAll()
    }, [currentActionPath]);

    useEffect(() => {
        const newActionPath = getActionPath();
        if (newActionPath != currentActionPath) {
            setCurrentActionPath(newActionPath)
        }

    }, [location]);

    // @ts-expect-error ignore type hint
    globalThis.setInformationContainerItem = setInformationContainerItem;
    // @ts-expect-error ignore type hint
    globalThis.setHeaderImage = setImage;

    return (
        <>
            <Helmet titleTemplate={'%s - ' + Constants.SITE_NAME}>
                <title>{currentRoute.title}</title>
                <meta name="description" content={currentRoute.metaDescription}/>
                <meta name="og:title" content={currentRoute.metaTitle}/>
                <meta name="og:description" content={currentRoute.metaDescription}/>
                <meta name="og:image"
                      content={Constants.SITE_URL + '/header-images/' + currentRoute.headerImagePath + '/small.png'}/>
                <link rel="canonical" href={currentCanonicalUrl}/>
                {currentRoute.path && currentRoute.path === '*' ? (
                    <meta name="robots" content="noindex"/>
                ) : null}
            </Helmet>
            <ReactNotifications isMobile={width < Constants.LayoutBreakpoints.tablet}/>
            <div className="App">
                <div className="layout">
                    <Header currentRoute={currentRoute} image={image}
                            informationContainerItem={informationContainerItem} width={width}/>
                    {width <= Constants.LayoutBreakpoints.desktop && currentRoute.index ? (
                        <TrustImages style={{
                            width: '100vw',
                            textAlign: 'center',
                            marginTop: headerOffset,
                            padding: '0 1.5rem',
                        }}/>
                    ) : null}
                    <main id="mainContent" style={{
                        top: currentRoute.index ? mainContentOffset : headerOffset,
                        marginBottom: (currentRoute.index ? mainContentOffset : headerOffset) + (width * 0.05)
                    }}>
                        <Outlet context={{width}}/>
                    </main>
                    <Footer width={width}/>
                </div>
            </div>
        </>
    )
}

export default Layout

export function useWidth() {
    return useOutletContext<ContextType>();
}
